// LICENSE_CODE ZON
'use strict'; /*jslint react:true es6:true*/
import React, {
    useState,
    useMemo,
    useEffect,
    useContext,
    useCallback,
    useImperativeHandle,
} from 'react';
import uniq from 'lodash4/uniq.js';
import {do_sort} from './utils.js';
import {SELECTION_KIND, MAX_LOG_SIZE} from './constants.js';

export const Lead_context = React.createContext(false);

export const Columns_context = React.createContext({
    count: [],
    inc: ()=>{},
    dec: ()=>{},
});

export const Meta_context = React.createContext({
    id_key: null,
    get_row_link: ()=>{},
    on_row_click: null,
});

export const Pagination_context = React.createContext({
    page: 1,
    page_size: null,
    infinity_scroll: false,
    has_more: true,
    list: [],
    next_page: ()=>{},
});

export const Selection_context = React.createContext({
    has_items: false,
    selected_ids: [],
    all_selected: false,
    enabled: false,
    toggle: ()=>{},
    select_many: ()=>{},
    set_selection_ext: ()=>{},
    toggle_all: ()=>{},
    is_selected: ()=>{},
    push_id: ()=>{},
    pop_id: ()=>{},
});

export const Sort_context = React.createContext({
    sorting: '',
    toggle: ()=>{},
});

export const Record_context = React.createContext(null);


function Selection_context_inner({children, enabled, on_change}, ref){
    const [ids, set_ids] = useState({});
    const [selected_ids, set_selected_ids] = useState([]);
    const [log, set_log] = useState([]);
    const push_log = useCallback(rec=>
        set_log(v=>[...v, rec].slice(-MAX_LOG_SIZE)), []);
    const value = useMemo(()=>({
        enabled,
        log,
        selected_ids,
        has_items: !!Object.keys(ids).length,
        all_selected: Object.keys(ids).length==selected_ids.length,
        is_selected: id=>selected_ids.includes(id),
        toggle: id=>{
            const is_add = !selected_ids.includes(id);
            const new_val = is_add
                ? [...selected_ids, id]
                : selected_ids.filter(s=>s!=id);
            set_selected_ids(new_val);
            on_change?.(new_val, {kind: SELECTION_KIND.SINGLE});
            push_log([{id, is_add}]);
        },
        select_many: list=>{
            const new_val = uniq([...selected_ids, ...list]);
            const log_items = new_val.map(v=>({id: v, is_add: true}));
            set_selected_ids(new_val);
            on_change?.(new_val, {kind: SELECTION_KIND.MULTIPLE});
            push_log(log_items);
        },
        set_selection_ext: list=>{
            set_selected_ids(list);
            on_change?.(list, {kind: SELECTION_KIND.EXTERNAL});
            push_log(list.map(id=>({id, is_add: true})));
        },
        toggle_all: ()=>{
            const all_ids = Object.keys(ids);
            const select_all = all_ids.length!=selected_ids.length;
            const new_val = select_all
                ? all_ids
                : [];
            const log_items = select_all
                ? all_ids.map(id=>({id, is_add: true}))
                : [];
            set_selected_ids(new_val);
            on_change?.(new_val, {kind: SELECTION_KIND.ALL});
            push_log(log_items);
        },
        clear: ()=>{
            set_selected_ids([]);
            on_change?.([], {kind: SELECTION_KIND.ALL});
            push_log([]);
        },
        push_id: id=>set_ids(val=>{
            const new_val = {...val};
            new_val[id] = true;
            return new_val;
        }),
        pop_id: id=>set_ids(val=>{
            const new_val = {...val};
            delete new_val[id];
            return new_val;
        }),
    }), [ids, log, selected_ids, on_change, enabled]);
    useImperativeHandle(ref, ()=>({
        clear: value.clear,
        set_selection: value.set_selection_ext,
    }), [value.clear, value.set_selected_ext]);
    return <Selection_context.Provider value={value}>
      {children}</Selection_context.Provider>;
}

export const Selection_context_wrapper =
    React.forwardRef(Selection_context_inner);

export function Sort_context_wrapper({
    children,
    list,
    sorting,
    default_sorting,
})
{
    const [sort, set_sort] = useState(default_sorting ?? null);
    useEffect(()=>{
        if (sorting)
            set_sort(sorting);
    }, [sorting]);
    const toggle = useCallback(field=>set_sort(val=>
        val==`${field}@19` ? `${field}@91` : `${field}@19`), []);
    const sorted_list = useMemo(()=>do_sort(list, sort), [list, sort]);
    const value = useMemo(()=>({
        sorting: sort,
        toggle,
        list: sorted_list,
    }), [sort, toggle, sorted_list]);
    return <Sort_context.Provider value={value}>
        {children}</Sort_context.Provider>;
}

export function Columns_context_wrapper({children}){
    const [count, set_count] = useState(0);
    const value = useMemo(()=>({
        count,
        inc: ()=>set_count(v=>v+1),
        dec: ()=>set_count(v=>v-1),
    }), [count]);
    return <Columns_context.Provider value={value}>
      {children}</Columns_context.Provider>;
}

export function Meta_context_wrapper({
    id_key,
    get_row_link,
    on_row_click,
    children,
})
{
    const value = useMemo(()=>({
        id_key,
        get_row_link,
        on_row_click,
    }), [id_key, get_row_link, on_row_click]);
    return <Meta_context.Provider value={value}>
      {children}</Meta_context.Provider>;
}

export function Pagination_context_wrapper({
    children,
    page_size,
    infinity_scroll,
})
{
    const {list} = useContext(Sort_context);
    const [page, set_page] = useState(1);
    const value = useMemo(()=>{
        const visible_items = page*page_size||list.length;
        const has_more = list.length>visible_items;
        return {
            page,
            page_size,
            has_more,
            infinity_scroll: !!infinity_scroll,
            list: has_more ? list.slice(0, visible_items) : list,
            next_page: ()=>set_page(v=>v+1),
        };
    }, [page_size, page, infinity_scroll, list]);
    return <Pagination_context.Provider value={value}>
      {children}</Pagination_context.Provider>;
}
